import { ApInvoiceTransactionStatusEnum } from '@/context/services/transaction_items/List/TransactionItems.service';
import {
  ForbiddenCodeEnum,
  isAuthError,
  isForbiddenError,
  isInvalidError,
  isPaymentRequiredError,
  isSuccess,
} from '@/context/services/type';
import { isCommonError } from '@/libs/typeguard/isError';
import { useQueryClient } from '@tanstack/react-query';
import {
  CommonError,
  PutApInvoiceTransactionGeneralInputMutationBody,
  PutApInvoiceTransactionGeneralInputMutationError,
  PutApInvoiceTransactionGeneralInputMutationResult,
  UnauthorizedResponse,
  getGetApInvoiceTransactionGeneralInputQueryKey,
  usePutApInvoiceTransactionGeneralInput,
} from 'ap-openapi';
import { useCallback } from 'react';

export type UpdateApInvoiceTransactionGeneralInputUnprocessableEntityResponse =
  {
    errors: {
      [key: string]: {
        message: string;
        type: string;
      };
    };
  };

type TUpdateTransactionItem = ReturnType<
  typeof usePutApInvoiceTransactionGeneralInput
>['mutateAsync'];
type UpdateTransactionItemMutateArgs = Parameters<TUpdateTransactionItem>['0'];

type UpdateApInvoiceTransactionGeneralInputSuccess = {
  status: ApInvoiceTransactionStatusEnum.SUCCESS;
};

type UpdateApInvoiceTransactionGeneralInputAuthError = {
  status: ApInvoiceTransactionStatusEnum.AUTH_ERROR;
  errors: UnauthorizedResponse;
  redirectPath: string;
};

type UpdateApInvoiceTransactionGeneralInputInvalidError = {
  status: ApInvoiceTransactionStatusEnum.INVALID_ERROR;
  errors: UpdateApInvoiceTransactionGeneralInputUnprocessableEntityResponse;
};

type UpdateApInvoiceTransactionGeneralInputCommonError = {
  status: ApInvoiceTransactionStatusEnum.COMMON_ERROR;
  errors: CommonError;
};

type UpdateApInvoiceTransactionGeneralInputProxyLoginError = {
  status: ApInvoiceTransactionStatusEnum.PROXY_LOGIN_ERROR;
  errors: CommonError;
};

type UpdateApInvoiceTransactionGeneralInputCorporateRequiredError = {
  status: ApInvoiceTransactionStatusEnum.CORPORATE_REQUIRED;
  errors: CommonError;
};

type UpdateApInvoiceTransactionGeneralInputResult =
  | UpdateApInvoiceTransactionGeneralInputSuccess
  | UpdateApInvoiceTransactionGeneralInputAuthError
  | UpdateApInvoiceTransactionGeneralInputInvalidError
  | UpdateApInvoiceTransactionGeneralInputCommonError
  | UpdateApInvoiceTransactionGeneralInputProxyLoginError
  | UpdateApInvoiceTransactionGeneralInputCorporateRequiredError;

export const useUpdateTransactionItem = (id: string) => {
  const queryClient = useQueryClient();
  const { mutateAsync: _mutateAsync } = usePutApInvoiceTransactionGeneralInput({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetApInvoiceTransactionGeneralInputQueryKey(id),
        });
      },
    },
  });

  const mutateAsync = useCallback(
    async (
      args: UpdateTransactionItemMutateArgs
    ): Promise<UpdateApInvoiceTransactionGeneralInputResult> => {
      const resp = await _mutateAsync(args);

      if (isSuccess(resp.status)) {
        queryClient.setQueryData(
          getGetApInvoiceTransactionGeneralInputQueryKey(id),
          { data: resp.data }
        );

        return {
          status: ApInvoiceTransactionStatusEnum.SUCCESS,
        };
      } else if (isAuthError(resp.status) || isForbiddenError(resp.status)) {
        // @ts-ignore - resp.data might not have code property in success types
        if (resp.data?.code === ForbiddenCodeEnum.FORBIDDEN_MIMIC_MODE) {
          return {
            status: ApInvoiceTransactionStatusEnum.PROXY_LOGIN_ERROR,
            errors: isCommonError(resp.data) ? resp.data : {},
          };
        }
        return {
          status: ApInvoiceTransactionStatusEnum.AUTH_ERROR,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          errors: (resp.data ?? {}) as UnauthorizedResponse,
          redirectPath: resp.headers?.location ?? '/',
        };
      } else if (isPaymentRequiredError(resp.status)) {
        return {
          status: ApInvoiceTransactionStatusEnum.CORPORATE_REQUIRED,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          errors: (resp.data ?? {}) as CommonError,
        };
      } else if (isInvalidError(resp.status)) {
        return {
          status: ApInvoiceTransactionStatusEnum.INVALID_ERROR,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          errors: (resp.data ??
            {}) as UpdateApInvoiceTransactionGeneralInputUnprocessableEntityResponse,
        };
      } else {
        return {
          status: ApInvoiceTransactionStatusEnum.COMMON_ERROR,
          errors: isCommonError(resp.data) ? resp.data : {},
        };
      }
    },
    [_mutateAsync, id, queryClient]
  );

  return { mutateAsync };
};

export type {
  PutApInvoiceTransactionGeneralInputMutationBody,
  PutApInvoiceTransactionGeneralInputMutationError,
  PutApInvoiceTransactionGeneralInputMutationResult,
  UpdateApInvoiceTransactionGeneralInputAuthError,
  UpdateApInvoiceTransactionGeneralInputCommonError,
  UpdateApInvoiceTransactionGeneralInputInvalidError,
  UpdateApInvoiceTransactionGeneralInputResult,
  UpdateTransactionItemMutateArgs,
};
